import React from 'react';
import { Box, Typography, IconButton, Tooltip, CircularProgress, LinearProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { getFileCategory, getFileIconConfig, formatFileSize } from './chatUtils';

const FileAttachmentDisplay = ({
  attachment,
  messageId,
  isSelf,
  downloadProgress,
  onDownload,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const category = getFileCategory(attachment?.fileType);
  const isDownloading = downloadProgress?.[messageId] !== undefined;
  const progress = downloadProgress?.[messageId] ?? 0;
  const { Icon: FileIcon, color: iconBg } = getFileIconConfig(category);

  if (category === 'image') {
    return (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Box
          component="img"
          src={attachment.url}
          alt={attachment.fileName}
          sx={{
            maxWidth: 240,
            maxHeight: 200,
            borderRadius: 2,
            display: 'block',
            objectFit: 'cover',
            border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
          }}
        />
        <Tooltip title="Download">
          <IconButton
            size="small"
            onClick={() => onDownload(attachment, messageId)}
            disabled={isDownloading}
            sx={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              bgcolor: alpha(theme.palette.common.black, 0.55),
              color: 'white',
              width: 28,
              height: 28,
              '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.75) },
              '&.Mui-disabled': { bgcolor: alpha(theme.palette.common.black, 0.4), color: 'white' },
            }}
          >
            {isDownloading ? (
              <CircularProgress size={14} sx={{ color: 'white' }} />
            ) : (
              <CloudDownloadIcon sx={{ fontSize: 15 }} />
            )}
          </IconButton>
        </Tooltip>
        {isDownloading && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              borderRadius: '0 0 8px 8px',
              overflow: 'hidden',
            }}
          >
            <LinearProgress variant="determinate" value={progress} sx={{ height: 3 }} />
          </Box>
        )}
        {attachment.fileName && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              fontSize: '0.7rem',
              color: isSelf ? 'rgba(255,255,255,0.7)' : 'text.secondary',
              maxWidth: 240,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {attachment.fileName}
          </Typography>
        )}
      </Box>
    );
  }

  if (category === 'video') {
    return (
      <Box>
        <Box
          component="video"
          src={attachment.url}
          controls
          preload="metadata"
          sx={{
            maxWidth: 280,
            borderRadius: 2,
            display: 'block',
            border: `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              color: isSelf ? 'rgba(255,255,255,0.75)' : 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 200,
            }}
          >
            {attachment.fileName}{' '}
            {attachment.fileSize ? `· ${formatFileSize(attachment.fileSize)}` : ''}
          </Typography>
          <Tooltip title={isDownloading ? `${progress}%` : 'Download'}>
            <IconButton
              size="small"
              onClick={() => onDownload(attachment, messageId)}
              disabled={isDownloading}
              sx={{
                color: isSelf ? 'rgba(255,255,255,0.85)' : 'primary.main',
                flexShrink: 0,
                '&.Mui-disabled': { color: isSelf ? 'rgba(255,255,255,0.4)' : 'action.disabled' },
              }}
            >
              {isDownloading ? (
                <CircularProgress size={14} />
              ) : (
                <CloudDownloadIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        {isDownloading && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 0.25, borderRadius: 4, height: 3 }}
          />
        )}
      </Box>
    );
  }

  // PDF / Excel / Word / Generic file
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor: isSelf
          ? alpha('#ffffff', 0.14)
          : isDark
          ? alpha('#ffffff', 0.07)
          : alpha(theme.palette.grey[500], 0.1),
        minWidth: 210,
        maxWidth: 270,
        border: `1px solid ${isSelf ? alpha('#ffffff', 0.15) : alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 1.5,
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 2px 8px ${alpha(iconBg, 0.4)}`,
        }}
      >
        <FileIcon sx={{ fontSize: 22, color: 'white' }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 600,
            fontSize: '0.78rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isSelf ? 'rgba(255,255,255,0.95)' : 'text.primary',
          }}
        >
          {attachment.fileName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            color: isSelf ? 'rgba(255,255,255,0.6)' : 'text.secondary',
          }}
        >
          {formatFileSize(attachment.fileSize)}
          {attachment.fileType &&
            ` · ${attachment.fileType.split('/')[1]?.toUpperCase()}`}
        </Typography>
        {isDownloading && (
          <Box sx={{ mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                borderRadius: 4,
                height: 3,
                bgcolor: isSelf ? 'rgba(255,255,255,0.2)' : alpha(theme.palette.primary.main, 0.15),
                '& .MuiLinearProgress-bar': { bgcolor: isSelf ? 'white' : 'primary.main' },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: isSelf ? 'rgba(255,255,255,0.6)' : 'text.secondary',
              }}
            >
              Downloading {progress}%
            </Typography>
          </Box>
        )}
      </Box>

      <Tooltip title={isDownloading ? `${progress}%` : 'Download'}>
        <IconButton
          size="small"
          onClick={() => !isDownloading && onDownload(attachment, messageId)}
          disabled={isDownloading}
          sx={{
            color: isSelf ? 'rgba(255,255,255,0.85)' : 'primary.main',
            flexShrink: 0,
            '&.Mui-disabled': { color: isSelf ? 'rgba(255,255,255,0.35)' : 'action.disabled' },
          }}
        >
          {isDownloading ? (
            <CircularProgress size={15} />
          ) : (
            <CloudDownloadIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default FileAttachmentDisplay;
